import './App.css';
import React, { useState, useEffect, useRef } from 'react';
import { Slideshow } from './SlideShow';

// Fades/slides children in when they enter the viewport — the mobile
// equivalent of the desktop's pixel-offset scroll animations, but
// independent of page height so it works on any screen size.
const Reveal = ({ children, delay = 0, from = 'up', style }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const hiddenTransform = {
    up: 'translateY(40px)',
    down: 'translateY(-40px)',
    left: 'translateX(-40px)',
    right: 'translateX(40px)'
  }[from];

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate(0, 0)' : hiddenTransform,
        transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms`,
        ...style
      }}
    >
      {children}
    </div>
  );
};

const Bullet = ({ children }) => (
  <p className='title-text' style={{ textAlign: 'left', margin: '0 0 12px' }}>• {children}</p>
);

const ExperienceEntry = ({ title, subtitle, date, bullets, tech, media }) => (
  <div style={{ padding: '0 24px', marginBottom: 90 }}>
    <Reveal from='left'>
      <p className='sub-signature' style={{ textAlign: 'left', marginBottom: 0 }}>{title}</p>
      <p className='title-text' style={{ textAlign: 'left', opacity: 0.6, margin: 0 }}>{subtitle}</p>
      <p className='title-text' style={{ textAlign: 'left', opacity: 0.6, marginBottom: 16 }}>{date}</p>
    </Reveal>
    <Reveal from='right' delay={150}>
      {bullets.map((bullet, index) => <Bullet key={index}>{bullet}</Bullet>)}
      {tech && <p className='title-text' style={{ textAlign: 'left', marginTop: 12 }}>{tech}</p>}
    </Reveal>
    <Reveal from='up' delay={250} style={{ marginTop: 24 }}>
      {media}
    </Reveal>
  </div>
);

function MobileApp() {
  const link = <a href="https://datascience.hiqsyd.se/" target="_blank" rel="noopener noreferrer">website</a>;

  const skills = [
    { name: 'Javascript', level: '****' },
    { name: 'React.js', level: '****' },
    { name: 'HTML/CSS', level: '****' },
    { name: 'Node.js', level: '***' },
    { name: 'Python', level: '***' },
    { name: 'R', level: '***' },
    { name: 'AWS', level: '**' },
    { name: 'Azure', level: '**' },
    { name: '.NET C#', level: '**' }
  ];

  const mobileCarouselStyle = { position: 'relative', left: 0 };

  return (
    <div style={{ width: '100%', overflowX: 'hidden', textAlign: 'center' }}>

      {/* Header */}
      <div style={{ background: 'linear-gradient(pink, #FFFFFF 90%)', position: 'relative', paddingBottom: 60 }}>
        <img
          src={require('./sakura4.png')}
          alt="sakura4"
          className='fade-in-soft'
          style={{ position: 'absolute', top: 0, right: 0, width: '65%', pointerEvents: 'none' }}
        />
        <div style={{ height: '18vh' }}></div>
        <p className='signature fade-in-load'>Yi Wang</p>
        <p className='sub-signature fade-in-load delay-1'>Software Developer</p>
        <div style={{ height: '12vh' }}></div>
      </div>

      {/* Introduction */}
      <div style={{ padding: '40px 24px' }}>
        <Reveal from='left'>
          <p className='sub-signature' style={{ opacity: 0.2 }}>INTRODUCTION</p>
        </Reveal>
        <Reveal from='up' delay={100}>
          <p className='title-text'>Software developer with data analysis skills.</p>
          <p className='title-text'>Strives to use data to improve the user experience by finding the most optimal way to guide the user's eyes.</p>
        </Reveal>
      </div>

      {/* Education */}
      <div style={{ padding: '40px 24px' }}>
        <Reveal from='right'>
          <p className='sub-signature' style={{ opacity: 0.2 }}>EDUCATION</p>
        </Reveal>
        <Reveal from='up' delay={100}>
          <p className='title-text'>MSc in Data Analytics and Business Economics at Lund University</p>
        </Reveal>
      </div>

      {/* Courses */}
      <div style={{ padding: '40px 24px' }}>
        <Reveal from='up'>
          <p className='sub-signature'>COURSES</p>
        </Reveal>
        {[
          { href: 'https://mau.se/en/study-education/courses/da204e/', label: 'Programming in C#(Malmö University)' },
          { href: 'https://www.udemy.com/course/react-redux/', label: 'Modern React with Redux' },
          { href: 'https://www.udemy.com/course/javascript-beginners-complete-tutorial/?srsltid=AfmBOoq2h1B4KgS8euLx8rslWD7mIuI-uZub1LC5OweH0rx_R00w9zBV', label: 'The Modern Javascript Bootcamp Course' },
          { href: 'https://www.udemy.com/course/python-for-data-science-and-machine-learning-bootcamp/', label: 'Python for Data Science and Machine Learning Bootcamp' }
        ].map((course, index) => (
          <Reveal key={course.label} from='up' delay={index * 120}>
            <p style={{ margin: '18px 0' }}>
              <a href={course.href} target="_blank" rel="noopener noreferrer" className='title-text'>{course.label}</a>
            </p>
          </Reveal>
        ))}
        <img
          src={require('./sakura3.png')}
          alt="sakura3"
          style={{ width: '80%', marginTop: 24, pointerEvents: 'none' }}
        />
      </div>

      {/* Experience */}
      <div style={{ padding: '40px 0' }}>
        <Reveal from='up'>
          <p className='sub-signature' style={{ marginBottom: 50 }}>EXPERIENCE</p>
        </Reveal>

        <ExperienceEntry
          title='Software Developer'
          subtitle='Playtech'
          date='October 2023 — Present'
          bullets={[
            'Developed and customized frontend features for white-label sports betting platforms using React Redux, JavaScript (ES6+), HTML5, and CSS3.',
            'Implemented responsive components and improved cross-browser compatibility for desktop and mobile devices.',
            'Collaborated with different teams to integrate APIs and enhance platform stability.',
            'Optimized component reusability through modular architecture and best practices.'
          ]}
          tech='Tech Stack & Tools: React Redux, JavaScript, HTML5, CSS3, REST APIs, Webpack, Git, Agile, Docker (basic understanding)'
          media={
            <Slideshow
              images={[
                require('./playtech1.jpg'),
                require('./playtech2.jpg'),
                require('./playtech3.jpg')
              ]}
              width='100%'
              height={260}
              containerStyle={mobileCarouselStyle}
            />
          }
        />

        <ExperienceEntry
          title='Frontend Assistant'
          subtitle='HiQ'
          date='July 2023 — August 2023'
          bullets={[
            <>Developed and maintained a data visualization {link} for electricity price predictions using React.js and modern web technologies.</>,
            'Collaborated with UX designers using Figma to implement intuitive and responsive user interfaces.',
            'Optimized data visualization components for real-time updates and improved user experience.',
            'Implemented responsive design principles ensuring compatibility across all devices.'
          ]}
          media={
            <img
              src={require('./elspargif.gif')}
              alt="elspargif"
              style={{ width: '100%', borderRadius: 8 }}
            />
          }
        />

        <ExperienceEntry
          title='AI IOS Developer'
          subtitle='Hobby Project'
          date='2026'
          bullets={[
            'Developed with the help of Claude Code in Visual Studio Code.',
            'A personal period-tracking app that predicts upcoming cycles, logs daily symptoms (cramps, mood, pain levels, notes), and visualizes cycle trends over time.',
            'Home-screen widget for quick period logging and countdown to the next expected period, plus automatic reminder notifications before and during predicted periods.',
            'Data backup/export, light/dark themes, and support for both English and Chinese.'
          ]}
          tech='Tech Stack & Tools: Swift, SwiftUI, iOS Widgets, XCode, Git.'
          media={
            <Slideshow
              images={[
                require('./periodtracker1.jpg'),
                require('./periodtracker2.jpg'),
                require('./periodtracker3.jpg'),
                require('./periodtracker4.jpg')
              ]}
              width='100%'
              height={460}
              containerStyle={mobileCarouselStyle}
            />
          }
        />
      </div>

      {/* Skills — staggered "falling" reveal like the desktop cascade */}
      <div style={{ padding: '40px 24px', position: 'relative' }}>
        <img
          src={require('./sakura7.png')}
          alt="sakura7"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', opacity: 0.35, zIndex: -1, pointerEvents: 'none' }}
        />
        <Reveal from='down'>
          <p className='sub-signature'>SKILLS</p>
        </Reveal>
        {skills.map((skill, index) => (
          <Reveal key={skill.name} from='down' delay={index * 100}>
            <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 320, margin: '0 auto' }}>
              <p className='title-text' style={{ margin: '4px 0' }}>{skill.name}</p>
              <p className='sub-signature' style={{ margin: '4px 0', fontSize: '1.2em' }}>{skill.level}</p>
            </div>
          </Reveal>
        ))}
        <Reveal from='up' delay={skills.length * 100}>
          <p className='title-text' style={{ marginTop: 24 }}>And loves to learn more!</p>
        </Reveal>
      </div>

      <img
        src={require('./sakura1.png')}
        alt="sakura1"
        style={{ width: '85%', pointerEvents: 'none', alignSelf: 'flex-start', display: 'block' }}
      />

      {/* Contact */}
      <div style={{ background: 'linear-gradient(#FFFFFF 10%, pink)', padding: '60px 24px 50px' }}>
        <Reveal from='up'>
          <p className='sub-signature'>Contact Me</p>
        </Reveal>
        <Reveal from='up' delay={150}>
          <a href="https://www.linkedin.com/in/yi-wang-9349b524b/" target="_blank" rel="noopener noreferrer">
            <img
              src={require('./LinkedIn_logo.png')}
              alt="LinkedIn"
              style={{ background: 'linear-gradient(#FFFFFF 20%, darkblue 1500%)', borderRadius: 120, width: 120, height: 120, margin: '30px 0' }}
            />
          </a>
        </Reveal>
        <Reveal from='up' delay={250}>
          <p className='sub-signature' style={{ fontSize: '1.1em', wordBreak: 'break-word' }}>jacquie.y.wang@gmail.com</p>
        </Reveal>
      </div>
    </div>
  );
}

export default MobileApp;
